import { collector } from "./collector";
import { transport } from "./transport";
import { startLifecycle } from "./collectors/lifecycle";
import { startClick } from "./collectors/click";
import { startConnectivity } from "./collectors/connectivity";
import { startErrors } from "./collectors/errors";
import { startNetwork } from "./collectors/network";
import { startIntegrity } from "./collectors/integrity";
import { startWebVitals } from "./webVitals";

// Public entry point for the eKYC web diagnostic tool. Anchored at EKYCManager so
// a single init() covers every document module (MyKad/MyPR/MyTentera/Passport/
// FaceLiveness). Capture is independent of whether the user clicks Contact support.

let teardowns = [];
let active = false;

export function initDiagnostics(validationId, ctx = {}) {
  if (active || !validationId) return;
  try {
    collector.configure(validationId, ctx);
  } catch (e) {
    return;
  }

  // Each collector is isolated: one failing must not break the others or the app.
  const safe = (fn) => {
    try {
      const teardown = fn();
      if (teardown) teardowns.push(teardown);
    } catch (e) {
      /* diagnostics must never break the eKYC flow */
    }
  };

  // Wrap fetch first so later requests are captured and the tamper sentinel
  // recognises our own wrapper.
  safe(startNetwork);
  safe(startErrors);
  safe(startLifecycle);
  safe(startClick);
  safe(startConnectivity);
  safe(startIntegrity);
  safe(startWebVitals);

  try {
    transport.start();
  } catch (e) {
    /* noop */
  }
  active = true;
}

export function shutdownDiagnostics() {
  if (!active) return;
  try {
    transport.flushOnHidden();
  } catch (e) {
    /* best effort */
  }
  transport.stop();
  for (const fn of teardowns) {
    try {
      if (fn) fn();
    } catch (e) {
      /* noop */
    }
  }
  teardowns = [];
  active = false;
  collector.reset();
}

// Screen/step timeline. Call on each eKYC step change (EKYCManager).
export function trackScreen(screen) {
  if (!active || screen == null) return;
  const prev = collector.getScreen();
  if (prev === screen) return;
  if (prev) collector.track("step_exit", { step: prev });
  collector.setScreen(screen);
  collector.track("step_enter", { step: screen });
}

export function trackButton(action, extra) {
  if (!active) return;
  collector.track("button_click", { action, ...(extra || {}) });
}

export function trackBootFailure(detail) {
  if (!active) return;
  collector.track("boot_failure", detail || {}, { critical: true });
}

// Browser-side document photo checks (#244). Log only: unlike
// collector.recordDocScanFailure, these never touch docScanFailCount, so they
// never bring up the Contact-support screen and never change the scan-failure
// counter that is synced from server responses.
export function trackPhotoRejected(detail) {
  if (!active) return;
  collector.track("doc_scan_failed", detail || {}, { critical: true });
}

export function trackPhotoAccepted(detail) {
  if (!active) return;
  collector.track("photo_accepted", detail || {}, { critical: true });
}

// Force-ship the buffer now (used by the Contact-support action).
export function flushDiagnostics() {
  try {
    transport.flushNow();
  } catch (e) {
    /* noop */
  }
}

export function isDiagnosticsActive() {
  return active;
}

export { diagnosticsStore, useDiagnosticsState, shouldShowContactSupport } from "./store";
